<template>
  <div :class="[thumbnailCount, 'form-group', 'attachments-area']">
    <!-- Individual file progress bars -->
    <div v-if="uploadingFiles.length > 0" class="upload-progress-container">
      <div v-for="file in uploadingFiles" :key="file.id" class="file-upload-progress">
        <div class="file-info">
          <span class="file-name">{{ truncateFileName(file.name) }}</span>
          <span class="file-progress-percent">{{ file.progress }}%</span>
        </div>
        <div class="file-progress-bar-wrapper">
          <div class="file-progress-bar" :style="`width: ${file.progress}%`"></div>
        </div>
      </div>
    </div>

    <!-- Legacy progress bar (kept for backwards compatibility) -->
    <div class="progress-bar" :style="progressBarStyle" v-if="uploadCount > 0 && uploadingFiles.length === 0"></div>

    <UploadLimitModal ref="uploadLimitModalRef"/>
    <PhotoLightbox
        :channelId="channelId"
        :channel="channel"
        :photo="currentPhoto"
        :photos="imageList"
        v-on:perform="perform"
    />
    <div v-if="!readOnly" class="upload-wrapper as-attachment">
      <div
          v-if="!readOnly"
          @click="showImageAttachmentPicker"
          class="upload-wrapper as-attachment"
      >
        <i class="fal fa-camera"></i>
        <input
            hidden
            ref="imageAttachmentPicker"
            type="file"
            multiple
            @change="onImageUpload"
            accept="image/*"
        />
      </div>
    </div>
    <div
        v-if="!readOnly"
        @click="showFileAttachmentPicker"
        class="upload-wrapper as-attachment"
    >
      <i class="fal fa-paperclip"></i>
      <input
          hidden
          ref="fileAttachmentPicker"
          type="file"
          multiple
          @change="onFileAttachmentUpload"
          :accept="acceptedTypes"
      />
    </div>
    <div v-if="isLoaded">
      <h2 v-if="theAttachments && theAttachments.length > 0" style="margin-bottom: 5px;">
        Attachments</h2>
      <AttachedFile
          ref="attachedFile"
          v-on:perform="perform"
          :newImageName="newImageName"
          :readOnly="readOnly"
          v-for="attachment in theAttachments"
          :attachment="attachment"
          :key="attachment.id"
          :rallyId="rallyId"
          :channelId="channelId"
          :channel="channel"
      />
    </div>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from "vuex";
import UploadLimitModal from "./UploadLimitModal.vue";
import {
  router,
  slugToTypeObjectMap,
  findDefaultChannelOfTypeForRally,
  kebabCase,
  ACCEPTED_FILE_TYPES
} from "../common";

export default {
  components: {
    UploadLimitModal
  },
  data() {
    return {
      fileLimitUploads: false,
      currentPhoto: null,
      uploadProgress: 0,
      tempKey: null,
      imgData: null,
      lightboxIndex: 0,
      lightboxVisible: false,
      fileData: null,
      isLoaded: false,
      uploadingFiles: [] // Track individual file uploads
    };
  },
  props:["content","channelId","rallyId","readOnly", "contextType","contextShare", "channel"],
  computed: {
    ...mapState("contents", ["uploadCount","allContentByContextTypeAndId", "contentById"]),
    ...mapState("rallies", ["rallyChannelsByRallyId"]),
    acceptedTypes() {
      return ACCEPTED_FILE_TYPES;
    },
    correctContextType() {
      if (!this.content){
        return "Message"
      } else if (this.content.attributes["type"] == "WallMessage" || this.content.attributes["activity-type"] == 'WallMessage')  {
        return "Message"
      } else if (this.content.attributes["type"] == "CalendarEvent" || this.content.attributes["activity-type"] == 'CalendarEvent') {
        return "Event"
      } else if (this.content.attributes["type"] == "PayableGroup" || this.content.attributes["type"] == 'GivePayableGroup' || this.content.attributes["type"] == 'PayableEvent') {
        return "Content"
      } else {
        if (this.content.attributes['activity-type']) {
          return this.content.attributes['activity-type']
        } else {
          return this.content.attributes["type"]
        }
      }
    },
    newOrExistingContentId() {
      if (this.content.attributes['activity-id']) {
        return this.content.attributes['activity-id']
      } else if (this.content.id) {
        return this.content.id;
      } else {
        var ct = this.contentById[this.content.tempKey]
        return ct ? ct.id : ""
      }
    },
    theAttachments() {
      var atts = this.allContentByContextTypeAndId[
          parseInt(this.newOrExistingContentId)
          ];
      if (atts && atts.length > 0) {
        atts = atts.filter(a => {
          return a.attributes["type"] == "Attachment" || a.attributes["type"] == "File" || a.attributes["type"] == "Binary";
        });
      }
      return atts && atts.length > 2 ? atts.slice(0, atts.length ) : !atts ? [] : atts;
    },
    newImageName() {
      return this.imgData ? this.imgData.info.name : "";
    },
    progressBarStyle() {
      if ( this.uploadCount == 0 ) {
        return 'display: none;';
      } else {
        return "width: " + this.uploadProgress + "%;upload-count:" + this.uploadCount;
      }
    },
    attachmentsLength() {
      return this.theAttachments ? this.theAttachments.length : 0;
    },
    thumbnailCount() {
      if (this.attachmentsLength == 1) {
        return "single";
      } else if (this.attachmentsLength == 2) {
        return "double";
      } else if (this.attachmentsLength > 2) {
        return "triple";
      } else {
        return "";
      }
    },
    imageList() {
      if (this.attachmentsLength > 0) {
        return this.theAttachments.map(a => {
          return a;
        });
      } else {
        return [];
      }
    }
  },
  created() {
    this.getAttachments()
  },
  watch: {
    allContentByContextTypeAndId: function(newById, oldById) {
      if (this.uploadProgress > 0) {
        this.uploadProgress = 95;
      }
    }
  },
  methods: {
    ...mapActions("contents", ["fetchContents", "uploadFile"]),
    ...mapActions("shares", [ "shareContent"]),

    truncateFileName(fileName) {
      if (!fileName) return '';
      if (fileName.length <= 30) return fileName;
      const ext = fileName.split('.').pop();
      const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
      const truncatedName = nameWithoutExt.substring(0, 25);
      return `${truncatedName}...${ext}`;
    },

    closeFileLimitModal(){
      this.fileLimitUploads = false;
    },

    displayMessageUploadProgress() {
      setTimeout(
          function() {
            if (this.uploadProgress <= 90) {
              this.uploadProgress += 3;
              this.displayMessageUploadProgress();
            } else {
              this.uploadProgress = 0;
            }
          }.bind(this),
          500
      );
    },

    async getAttachments() {
      await this.fetchContents({
        contentType: "Binary",
        type: ["Attachment", "File"],
        contextRallyId: this.rallyId,
        contextChannelId: this.channelId,
        contextId: this.newOrExistingContentId,
        contextType: this.correctContextType,
        include: "shares",
        pageNumber: 1,
        customPageSize: 100,
        sort: "content.id",
        ignorePagination: true
      });
      this.isLoaded = true
    },

    perform(method, params) {
      params ? this[method](params) : this[method]();
    },

    showLightbox(image) {
      this.currentPhoto = image;
    },

    handleLightboxHide() {
      this.currentPhoto = null;
    },

    onImageUpload(e) {
      if (e.target.files.length > 10){
        this.$refs.uploadLimitModalRef.showModal()
      } else {
        for (var i = 0; i < e.target.files.length; i++){
          this.uploadFileAttachment(e.target.files[i], this.newOrExistingContentId, this.channelId, this.correctContextType, "Photo");
        }
      }
    },

    onFileAttachmentUpload(e) {
      if (e.target.files.length > 10){
        this.$refs.uploadLimitModalRef.showModal()
      } else {
        for (var i = 0; i < e.target.files.length; i++){
          this.uploadFileAttachment(e.target.files[i], this.newOrExistingContentId, this.channelId, this.correctContextType);
        }
      }
    },

    showFileAttachmentPicker() {
      this.$refs.fileAttachmentPicker.click();
    },

    showImageAttachmentPicker() {
      this.$refs.imageAttachmentPicker.click();
    },

    uploadFileAttachment(f, contextId, chanId, contextType, type = "Attachment") {
      // Create a unique ID for this upload
      const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      // Add to tracking array
      this.uploadingFiles.push({
        id: uploadId,
        name: f.name,
        progress: 0
      });

      let reader = new FileReader();
      this.fileData = f;
      reader.onload = e => {
        var params = {
          contentType: "Binary",
          contextId,
          contextType,
          type: "Attachment",
          contextChannelId: chanId,
          contextRallyId: this.rallyId,
          attachment: reader.result.split(",")[1],
          attachmentContentType: f.type,
          attachmentFileName: f.name,
          fileData: f,
          fileName: f.name, // Add fileName for error handling
          // Add progress callback
          onProgress: (progress) => {
            this.updateFileProgress(uploadId, progress);
          }
        };

        // No need for displayMessageUploadProgress anymore
        this.uploadFile(params).then(() => {
          // Remove from tracking array on success
          this.removeUploadingFile(uploadId);
        }).catch((error) => {
          // Remove from tracking array on error
          this.removeUploadingFile(uploadId);
          console.error('Upload failed:', error);
        });
      };

      try {
        reader.readAsDataURL(f);
      } catch (e) {
        console.log("upload file error....")
        console.log(e);
        this.removeUploadingFile(uploadId);
      }
    },

    updateFileProgress(uploadId, progress) {
      const fileIndex = this.uploadingFiles.findIndex(f => f.id === uploadId);
      if (fileIndex !== -1) {
        // Vue 3 way - just update the property directly
        this.uploadingFiles[fileIndex].progress = progress;
      }
    },

    removeUploadingFile(uploadId) {
      this.uploadingFiles = this.uploadingFiles.filter(f => f.id !== uploadId);
    },

    uploadImageMessageAttachment(img) {
      this.tempKey = "TK" + Math.floor(Math.random() * 10000);
      this.imgData = img;

      // Create upload tracking for image
      const uploadId = `upload-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      this.uploadingFiles.push({
        id: uploadId,
        name: img.info.name,
        progress: 0
      });

      this.uploadFile({
        rallyId: this.rallyId,
        contextRallyId: this.rallyId,
        contentType: "Binary",
        contextId: this.newOrExistingContentId,
        contextType: this.correctContextType,
        type: "Attachment",
        contextChannelId: this.channelId,
        attachment: img.dataUrl.split(",")[1],
        attachmentContentType: img.info.type,
        attachmentFileName: img.info.name,
        tempKey: this.tempKey,
        fileData: img,
        fileName: img.info.name,
        onProgress: (progress) => {
          this.updateFileProgress(uploadId, progress);
        }
      }).then(() => {
        this.removeUploadingFile(uploadId);
      }).catch(() => {
        this.removeUploadingFile(uploadId);
      });
    },

    saveAttachments() {
      let galleryChan = findDefaultChannelOfTypeForRally(
          "Gallery",
          this.rallyChannelsByRallyId,
          this.rallyId
      );
      let fileChan = findDefaultChannelOfTypeForRally(
          "Files",
          this.rallyChannelsByRallyId,
          this.rallyId
      );

      if (this.theAttachments && this.theAttachments.length > 0){
        this.theAttachments.forEach(a => {
          if (a && a.copySettings && a.copySettings !== "none"){
            let chan2Share2 = a.attributes["file-type"] == "Photo" ?  galleryChan : fileChan
            if(a.copySettings === 'root') {
              this.shareContent({
                contentUid: a.id,
                channelIds: [chan2Share2],
                rallyIds: [this.rallyId],
                shareType: 'original',
                status: 1,
                owner : 1
              })
            } else {
              this.shareContent({
                contentUid: a.id,
                channelIds: [chan2Share2],
                contextId: a.copySettings,
                contextType: 'Container',
                rallyIds: [this.rallyId],
                shareType: 'original',
                status: 1,
                owner : 1
              })
            }
          }
        })
      }
    },
  },
  mounted() {
    if (!this.content) {
      this.attachmentsFromTheStore = [];
    }
  }
}
</script>

<style scoped>
.upload-progress-container {
  margin: 10px 0;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 4px;
}

.file-upload-progress {
  margin-bottom: 10px;
}

.file-upload-progress:last-child {
  margin-bottom: 0;
}

.file-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 5px;
  font-size: 14px;
}

.file-name {
  color: #333;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 80%;
}

.file-progress-percent {
  color: #666;
  font-weight: bold;
  min-width: 45px;
  text-align: right;
}

.file-progress-bar-wrapper {
  width: 100%;
  height: 6px;
  background: #e9ecef;
  border-radius: 3px;
  overflow: hidden;
}

.file-progress-bar {
  height: 100%;
  background: #007bff;
  transition: width 0.3s ease;
  border-radius: 3px;
}

/* Keep existing styles */
.progress-bar {
  transition: width 0.5s ease;
}

.attachments-area {
  position: relative;
}
</style>